// @ts-nocheck
/**
 * Ludipek B2B price display.
 *
 * On page load, collect visible product NetSuite IDs from `[data-b2b-price][data-netsuite-id]`
 * spans. Partition into cache hits and misses via window.LudipekB2BCache. POST the misses to
 * {apiBaseUrl}/products/prices/fetch and render the returned unit prices into the matching elements.
 *
 * Per-item failures are non-blocking: lines with status !== 'ok' render their unpriced label
 * and get a `.b2b-unpriced` class. A failed request leaves prices already on screen (stale cache
 * paints) alone, and puts every price that had nothing to show into the failed state
 * (data-b2b-price-state="failed"; see markFailed). The price-skeleton watchdog in
 * layout/theme.liquid renders that state as a retry button, and window.LudipekB2BPricing.retry
 * is what the button calls.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  // Selectors:
  //   [data-b2b-price]              — single-price layout (non-clearance products): write the
  //                                    API-resolved unit price.
  //   [data-b2b-clearance-price]    — main number on a clearance card: write
  //                                    api_price × fecha_corta_discount_factor.
  //   [data-b2b-clearance-original] — strikethrough on a clearance card: write the api_price
  //                                    unchanged. Pairs with [data-b2b-clearance-price].
  //   [data-b2b-line-total]         — cart-row line total: write
  //                                    api_price × Number(element.dataset.quantity). The
  //                                    data-quantity attribute is re-rendered by Shopify on
  //                                    every cart mutation, and `cart:updated` retriggers the
  //                                    overlay. Once a signature covers the cart, updateCartTotals
  //                                    repaints the rows with the signed prices checkout charges.
  // The ancestor walk to [data-netsuite-id] bridges the inner price span to the product root.
  const PRICE_SELECTOR =
    '[data-b2b-price],[data-b2b-clearance-price],[data-b2b-clearance-original],[data-b2b-line-total]';
  const UNPRICED_CLASS = 'b2b-unpriced';
  const SHIMMER_BODY_CLASS = 'b2b-pricing-pending';
  const FETCH_TIMEOUT_MS = 20_000;
  const BASE_PRICE_EVENT = 'b2b:base-price-updated';
  // Shared with the price-skeleton watchdog in layout/theme.liquid, which also sets "slow" and gives
  // up ("failed") on a skeleton that outlives its bound. The state only ever rides on a skeleton
  // (data-b2b-loading="true"), and every rule for it requires one, so whichever script paints the
  // price just removes data-b2b-loading, as it always has, and the state stops applying.
  const PRICE_STATE_ATTRIBUTE = 'data-b2b-price-state';
  const FAILED_STATE = 'failed';
  // Horizon re-renders the cart by morphing its section in place: the same elements survive, with the
  // server's text and attributes put back and every attribute the server markup lacks removed. Nothing
  // is added, so the added-nodes scan below never fired, and the one-shot collectedElements set skipped
  // the unit prices on the next render anyway. After each sign the cart signer asks Horizon for such a
  // re-render (discount:update), so every quantity change left the rows on Shopify's numbers and the
  // total on its server skeleton, which the watchdog then turned into "Precio no disponible". Every
  // element this file writes carries this marker; the morph stripping it is the signal to paint it again.
  const PAINTED_ATTRIBUTE = 'data-b2b-painted';
  // What the cart's Neto, tax and total show when there is no amount to show (see updateCartTotals).
  const NO_AMOUNT_TEXT = '—';
  // The payload of the newest /cart/sign answer b2b-cart-snapshot.js wrote to the cart (b2b:cart-signed), '' when
  // it cleared the attributes. updateCartTotals also reads the one the server rendered into the cart summary.
  let latestSignedPayload = '';

  // Mirrors backend constants.FECHA_CORTA_DEFAULT_DISCOUNT_PCT — the bootstrap exposes this so
  // PDP and card can preview the discounted price live. Default 1.0 = no discount when missing.
  const FECHA_CORTA_FACTOR = Number(
    (BOOTSTRAP && BOOTSTRAP.fechaCortaDiscountFactor) || 1,
  );

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-pricing]', message);
    } else {
      console.debug('[b2b-pricing]', message, details);
    }
  }

  function logWarn(message, details) {
    if (details === undefined) {
      console.warn('[b2b-pricing]', message);
    } else {
      console.warn('[b2b-pricing]', message, details);
    }
  }

  function clearShimmer() {
    document.body.classList.remove(SHIMMER_BODY_CLASS);
  }

  function bootstrapIsUsable() {
    if (BOOTSTRAP === null) {
      logWarn('window.__LUDIPEK_B2B__ not present — skipping price display');
      return false;
    }
    if (!BOOTSTRAP.apiBaseUrl) {
      logWarn('apiBaseUrl is empty — set "Ludipek API base URL" in theme settings');
      return false;
    }
    if (!getEffectiveCountry()) {
      logWarn('effective country is empty — cannot determine pricing market');
      return false;
    }
    return true;
  }

  function resolveNetsuiteId(element) {
    const own = element.getAttribute('data-netsuite-id');
    if (own) return own;
    const ancestor = element.closest('[data-netsuite-id]');
    return ancestor ? ancestor.getAttribute('data-netsuite-id') : null;
  }

  // Track elements we've already collected so MutationObserver re-scans don't re-fetch.
  // WeakSet so detached DOM nodes are garbage-collected.
  const collectedElements = new WeakSet();

  function collectPriceElements(root) {
    const elements = Array.from(root.querySelectorAll(PRICE_SELECTOR));
    const byProductId = new Map();
    elements.forEach((element) => {
      // Line totals depend on data-quantity which Shopify mutates in place on +/- clicks
      // without replacing the DOM node, so we always re-process them; other price elements
      // are one-shot and skip via the WeakSet to avoid redundant work.
      const isLineTotal = element.hasAttribute('data-b2b-line-total');
      if (!isLineTotal && collectedElements.has(element)) return;
      const netsuiteId = resolveNetsuiteId(element);
      if (!netsuiteId) return;
      const productId = Number(netsuiteId);
      if (!Number.isFinite(productId) || productId <= 0) return;
      if (!isLineTotal) collectedElements.add(element);
      // Show shimmer until the API resolves this element. The body.b2b-pricing-pending class
      // covers the first paint, but lazy-loaded cards (paginated grid, recommendations) appear
      // after that class is cleared — without an explicit per-element marker they'd flash the
      // server-rendered $0 until the fetch returns. data-b2b-loading is the supported override.
      element.setAttribute('data-b2b-loading', 'true');
      // Collecting a failed element again is a retry: back to the plain skeleton. A "slow" state is
      // left alone — line totals are re-collected on every render, and the wait is still the same one.
      if (element.getAttribute(PRICE_STATE_ATTRIBUTE) === FAILED_STATE) {
        element.removeAttribute(PRICE_STATE_ATTRIBUTE);
      }
      if (!byProductId.has(productId)) {
        byProductId.set(productId, []);
      }
      byProductId.get(productId).push(element);
    });
    return byProductId;
  }

  // Format a currency amount per market convention. CLP → "$14.087" (integer, dot-grouped —
  // Chilean style). PEN → "S/ 1,234.50" (always 2 decimals per client feedback — comma
  // thousands, dot decimals, Peruvian style; the NBSP keeps the symbol from wrapping away from
  // the number). Unknown currencies fall back to the ISO code as a suffix so we never invent a
  // symbol we can't read back. Manual grouping instead of Intl.NumberFormat because es-CL
  // defaults minimumGroupingDigits to 2, so 4-digit values like 3470 render without a separator
  // while 5-digit values like 14087 do — inconsistent display.
  function formatCurrency(unitPrice, currencyName) {
    if (unitPrice == null) return '';
    const asNumber = Number(unitPrice);
    if (!Number.isFinite(asNumber)) return String(unitPrice);
    if (currencyName === 'PEN') {
      const [wholePart, decimalPart] = asNumber.toFixed(2).split('.');
      const groupedWhole = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `S/ ${groupedWhole}.${decimalPart}`;
    }
    const integer = Math.round(asNumber);
    const grouped = integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (!currencyName) return grouped;
    if (currencyName === 'CLP') return `$${grouped}`;
    return `${grouped} ${currencyName}`;
  }

  // How many minor units make one unit of the currency: PEN has céntimos, CLP has none.
  function minorUnitScale(currencyName) {
    return currencyName === 'PEN' ? 100 : 1;
  }

  // An amount cut down (not rounded) to the currency's minor unit, as /cart/sign's int() does with every unit price
  // it signs. Rounding would show one peso more than the buyer is charged on prices like 5864 x 0.7 = 4104.8. The
  // epsilon absorbs float noise on exact products (1000 x 0.7 = 699.9999999999999).
  function toWholeMinorUnits(amount, currencyName) {
    const scale = minorUnitScale(currencyName);
    return Math.floor(amount * scale + 1e-6) / scale;
  }

  // The Fecha corta price exactly as /cart/sign charges it: the regular price times the factor, in whole minor units.
  function fechaCortaUnitPrice(unitPriceNumber, currencyName) {
    return toWholeMinorUnits(unitPriceNumber * FECHA_CORTA_FACTOR, currencyName);
  }

  // A cart row's quantity (data-quantity, re-rendered by Shopify on every cart mutation), 1 when unreadable.
  function rowQuantity(element) {
    const quantity = Number(element.dataset.quantity || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function applyLine(elements, line) {
    // A zero/negative "price" is a NetSuite data placeholder, never a sellable price — render
    // the unpriced label instead of "$0" (defense-in-depth; the API also drops these rows).
    if (line && line.status === 'ok' && !(Number(line.unit_price) > 0)) {
      logWarn('non-positive unit price treated as unpriced', {
        product_id: line.product_id,
        unit_price: line.unit_price,
      });
      line = { ...line, status: 'unpriced' };
    }
    if (line && line.status === 'ok') {
      const unitPriceNumber = Number(line.unit_price);
      const regularFormatted = formatCurrency(line.unit_price, line.currency_name);
      const fechaCortaUnit = Number.isFinite(unitPriceNumber)
        ? fechaCortaUnitPrice(unitPriceNumber, line.currency_name)
        : NaN;
      const clearanceFormatted = Number.isFinite(fechaCortaUnit)
        ? formatCurrency(fechaCortaUnit, line.currency_name)
        : regularFormatted;
      elements.forEach((element) => {
        // [data-b2b-clearance-price]    → discounted clearance price (api × FC factor)
        // [data-b2b-clearance-original] → regular price as strikethrough (api unchanged)
        // [data-b2b-line-total]         → unit price × cart-row quantity
        // [data-b2b-price]              → regular price (single-price layout)
        // data-b2b-price-multiple (opt-in, grouped products) → the displayed price is the GROUP price
        // (per-unit × N). Only affects the displayed price/clearance figures; the line-total stays a raw
        // unit × cart-quantity product (the cart quantity is already in base units), and the dispatched
        // base-price event still carries the raw per-unit price.
        const isClearancePrice = element.hasAttribute('data-b2b-clearance-price');
        const isLineTotal = element.hasAttribute('data-b2b-line-total');
        // Cart rows carry the lot of the variant in the cart; a Fecha corta row is charged the clearance
        // price, so it must show (and total) that price, not the product's regular one. Either lot's unit is
        // cut to whole minor units, as /cart/sign signs it. This is only the first paint: once a signature
        // covers the cart, updateCartTotals repaints the rows with the signed prices (quantity breaks included).
        const isCartRow = element.hasAttribute('data-b2b-lot-status');
        const isFechaCortaRow = element.dataset.b2bLotStatus === 'fecha_corta';
        const rowUnit = isFechaCortaRow ? fechaCortaUnit : toWholeMinorUnits(unitPriceNumber, line.currency_name);
        const priceMultiple = Number(element.dataset.b2bPriceMultiple) || 1;
        let formatted = regularFormatted;
        if (isClearancePrice) {
          formatted = Number.isFinite(fechaCortaUnit)
            ? formatCurrency(fechaCortaUnit * priceMultiple, line.currency_name)
            : clearanceFormatted;
        } else if (isLineTotal && Number.isFinite(rowUnit)) {
          const lineTotal = rowUnit * rowQuantity(element);
          formatted = formatCurrency(lineTotal, line.currency_name);
          element.dataset.b2bLineTotalAmount = String(lineTotal);
          element.dataset.b2bCurrencyName = String(line.currency_name || '');
        } else if (isCartRow && Number.isFinite(rowUnit)) {
          formatted = formatCurrency(rowUnit * priceMultiple, line.currency_name);
        } else if (priceMultiple > 1 && Number.isFinite(unitPriceNumber)) {
          formatted = formatCurrency(unitPriceNumber * priceMultiple, line.currency_name);
        }
        element.textContent = formatted;
        element.setAttribute(PAINTED_ATTRIBUTE, '');
        element.classList.remove(UNPRICED_CLASS);
        element.removeAttribute('data-b2b-loading');
        element.removeAttribute(PRICE_STATE_ATTRIBUTE);
      });
      if (Number.isFinite(unitPriceNumber)) {
        document.dispatchEvent(
          new CustomEvent(BASE_PRICE_EVENT, {
            detail: { productId: Number(line.product_id), unitPrice: unitPriceNumber },
          }),
        );
      }
    } else {
      // Unpriced (API returned status !== 'ok'): write the customer-facing label directly into
      // textContent (each element carries it as data-b2b-unpriced-label) so the natural inline
      // flow handles the layout. Earlier we used ::before-with-position:absolute to overlay the
      // label, but the absolute positioning made the label collapse into the original (small)
      // span box, producing wrapped italics overlapping the unit suffix. Direct text is cleaner.
      //
      // [data-b2b-clearance-original] (strikethrough) gets emptied instead — there's no
      // meaningful regular-price strikethrough when even the regular price is unresolved.
      // CSS hides the empty .lp-product-card__list-price wrapper.
      elements.forEach((element) => {
        if (element.hasAttribute('data-b2b-clearance-original')) {
          element.textContent = '';
        } else {
          const label = element.getAttribute('data-b2b-unpriced-label') || '';
          if (label) element.textContent = label;
        }
        element.setAttribute(PAINTED_ATTRIBUTE, '');
        element.classList.add(UNPRICED_CLASS);
        element.removeAttribute('data-b2b-loading');
        element.removeAttribute(PRICE_STATE_ATTRIBUTE);
      });
      document.dispatchEvent(
        new CustomEvent(BASE_PRICE_EVENT, {
          detail: { productId: line ? Number(line.product_id) : null, unitPrice: null },
        }),
      );
    }
  }

  function getSelectedRegion() {
    return window.LudipekB2BLocation ? window.LudipekB2BLocation.getStoreCode() : null;
  }

  // Pricing's customer_id is the NS customer associated with the buyer's *currently selected*
  // location — never a per-person scalar. See [[multi-company-contacts]] / the per-location
  // metafield design: the same person at SCL vs CCP resolves to different NS customers and
  // therefore different price levels.
  function getCustomerIdForPricing() {
    return window.LudipekB2BLocation ? window.LudipekB2BLocation.getCurrentNetsuiteCustomerId() : null;
  }

  function getEffectiveCountry() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getEffectiveCountry) {
      return window.LudipekB2BLocation.getEffectiveCountry();
    }
    return BOOTSTRAP ? BOOTSTRAP.storeCountry : null;
  }

  async function fetchQuotes(productIds) {
    const region = getSelectedRegion();
    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/products/prices/fetch`;
    const body = {
      store_country: getEffectiveCountry(),
      customer_id: getCustomerIdForPricing(),
      product_ids: productIds,
      region: region,
    };
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': BOOTSTRAP.apiShopifyKey,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // A price we could not fetch keeps its skeleton attribute and gains data-b2b-price-state="failed".
  // The watchdog in layout/theme.liquid then shows a "Precio no disponible — reintenta" button in its
  // place instead of a skeleton that shimmers forever. The skeleton attribute stays on purpose: a
  // retry or a slower answer still paints through the usual path, which clears both.
  //
  // Add-to-cart gating is untouched: .b2b-unpriced (which disables a card's add button) means "the
  // API says there is no price", and a network failure is not that. The PDP keeps its add button
  // disabled until a price event arrives, as before.
  function markFailed(byProductId, productIds) {
    if (productIds.length === 0) return;
    const failedIds = new Set(productIds.map(Number));
    failedIds.forEach((productId) => {
      (byProductId.get(productId) || []).forEach((element) => {
        if (element.getAttribute('data-b2b-loading') !== 'true') return;
        element.setAttribute(PRICE_STATE_ATTRIBUTE, FAILED_STATE);
        // Let go of it so the next render cycle (a cart update, lazy-loaded cards) retries it.
        collectedElements.delete(element);
      });
    });
    // Skeletons another script paints from our answer (the PDP price card waits for
    // b2b:base-price-updated) would otherwise wait for the watchdog to give up on them. The cart total
    // is left to updateCartTotals, which knows whether it depends on a failed line.
    document.querySelectorAll('[data-b2b-loading="true"]').forEach((element) => {
      if (element.matches(PRICE_SELECTOR) || element.hasAttribute('data-b2b-cart-total')) return;
      if (failedIds.has(Number(resolveNetsuiteId(element)))) {
        element.setAttribute(PRICE_STATE_ATTRIBUTE, FAILED_STATE);
      }
    });
  }

  // A /cart/sign payload (base64url JSON without padding, the bytes the discount Function decodes), or null when
  // it is missing or unreadable.
  function decodeSignedPayload(payloadBase64) {
    if (!payloadBase64) return null;
    try {
      const base64 = String(payloadBase64).replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      const readable = payload && typeof payload.currency === 'string' && payload.lines && typeof payload.lines === 'object';
      return readable ? payload : null;
    } catch {
      return null;
    }
  }

  // The key a cart line is signed under, derived exactly as domain/cart_signing.signed_line_key, the discount
  // Function and the Sales Order builder derive it: the NetSuite id, plus ":fecha_corta" for a Fecha corta line.
  function signedLineKey(netsuiteId, lotStatus) {
    return lotStatus === 'fecha_corta' ? `${netsuiteId}:fecha_corta` : String(netsuiteId);
  }

  // The signed payload that prices every cart row at its current quantity, or null when none does. The candidates
  // are the payload this tab's signer wrote last (b2b:cart-signed) and the one the server rendered into the cart
  // summary (cart.attributes.b2b_payload, for a page that has not signed yet); the newest one that covers wins. A
  // payload covers the rows when each row's signed line key is in it with that row's quantity: the check the
  // discount Function makes on each cart line before it discounts it, so a signature for other quantities prices
  // nothing, and neither would checkout.
  function coveringSignedCart(lineElements) {
    const summary = document.querySelector('[data-b2b-signed-payload]');
    const candidates = [latestSignedPayload, summary ? summary.getAttribute('data-b2b-signed-payload') : '']
      .map(decodeSignedPayload)
      .filter((payload) => payload !== null)
      .sort((left, right) => (Number(right.generated_at) || 0) - (Number(left.generated_at) || 0));
    const covers = (payload) =>
      lineElements.every((lineElement) => {
        const netsuiteId = resolveNetsuiteId(lineElement);
        const line = netsuiteId ? payload.lines[signedLineKey(netsuiteId, lineElement.dataset.b2bLotStatus)] : null;
        return Boolean(line) && Number(line.qty) === rowQuantity(lineElement) && Number.isInteger(line.unit_price_minor);
      });
    return candidates.find(covers) || null;
  }

  // Paints one cart row with what checkout charges for it: its signed unit price, and that price x its quantity.
  function paintSignedRow(lineElement, unitPrice, lineTotal, currencyName) {
    lineElement.textContent = formatCurrency(lineTotal, currencyName);
    lineElement.dataset.b2bLineTotalAmount = String(lineTotal);
    lineElement.dataset.b2bCurrencyName = currencyName;
    lineElement.setAttribute(PAINTED_ATTRIBUTE, '');
    const row = lineElement.closest('tr[data-key]');
    if (row === null) return;
    row.querySelectorAll('[data-b2b-price]').forEach((unitElement) => {
      unitElement.textContent = formatCurrency(unitPrice, currencyName);
      unitElement.setAttribute(PAINTED_ATTRIBUTE, '');
    });
  }

  // The cart summary: Neto, the tax and Total estimado, painted from what checkout will charge. The display quotes
  // painted on the rows are priced at quantity 1 with every decimal, while /cart/sign prices each line at its cart
  // quantity (a quantity break can apply) and cuts the unit to whole minor units, so a sum of the display rows
  // misstated the total. The summary is therefore worked out from the signed payload that covers the cart
  // (coveringSignedCart), and the rows are repainted from it, so Neto is still the sum of the rows.
  //
  // In order, the exit paths are:
  // - no cart rows: the total's skeleton is cleared, Neto and the tax show a dash;
  // - a row whose price could not be fetched: the total goes to the failed state (a retry button) and Neto and the
  //   tax show a dash;
  // - a row with no B2B price (unpriced, or no NetSuite id): Shopify's total (the ceiling) is left in place, because
  //   that line pays the ceiling at checkout anyway, and Neto and the tax show a dash;
  // - no signature covers the cart (a sign is on its way, under the signer's skeleton, or it failed, and the checkout
  //   guard says why): the total, Neto and the tax show a dash, never a figure checkout will not charge;
  // - otherwise: Neto is the sum of the signed lines, the tax is Neto x rate rounded to the currency's minor unit
  //   (#1017: 125.974 -> 23.935), and the total is their sum. A rate for another country than the prices' (see
  //   cartTaxRate) leaves the tax a dash and the total at Neto.
  //
  // Every exit path clears the skeletons it leaves and marks them painted, so a re-render that puts the server
  // skeleton back strips the marker and brings the summary through here again. The summary also repaints when the
  // signer reports a new signature (b2b:cart-signed).
  function updateCartTotals() {
    const totalElements = document.querySelectorAll('[data-b2b-cart-total]');
    if (totalElements.length === 0) return;
    const netElements = document.querySelectorAll('[data-b2b-cart-net]');
    const taxElements = document.querySelectorAll('[data-b2b-cart-tax]');
    const clearTotalSkeleton = () => settlePaint(totalElements, null);
    const settleBreakdownWithoutAmounts = () => {
      settlePaint(netElements, NO_AMOUNT_TEXT);
      settlePaint(taxElements, NO_AMOUNT_TEXT);
    };
    const paintTotal = (formatted) => {
      totalElements.forEach((element) => {
        element.textContent = formatted;
        if (element.tagName === 'TEXT-COMPONENT') {
          element.setAttribute('value', formatted);
        }
      });
      clearTotalSkeleton();
    };
    const lineElements = Array.from(document.querySelectorAll('[data-b2b-line-total]'));
    if (lineElements.length === 0) {
      clearTotalSkeleton();
      settleBreakdownWithoutAmounts();
      return;
    }
    const hasFailedLine = lineElements.some(
      (lineElement) =>
        lineElement.getAttribute('data-b2b-loading') === 'true' &&
        lineElement.getAttribute(PRICE_STATE_ATTRIBUTE) === FAILED_STATE,
    );
    if (hasFailedLine) {
      totalElements.forEach((element) => {
        element.setAttribute(PAINTED_ATTRIBUTE, '');
        element.setAttribute('data-b2b-loading', 'true');
        element.setAttribute(PRICE_STATE_ATTRIBUTE, FAILED_STATE);
      });
      // The total's retry button speaks for the whole summary; a button per row would be three.
      settleBreakdownWithoutAmounts();
      return;
    }
    if (lineElements.some((lineElement) => lineElement.dataset.b2bLineTotalAmount === undefined)) {
      clearTotalSkeleton();
      settleBreakdownWithoutAmounts();
      return;
    }
    const signed = coveringSignedCart(lineElements);
    if (signed === null) {
      paintTotal(NO_AMOUNT_TEXT);
      settleBreakdownWithoutAmounts();
      return;
    }
    const currencyName = signed.currency;
    const scale = minorUnitScale(currencyName);
    // In whole minor units throughout, so float noise on céntimos can never move a sum or the tax's rounding.
    let netMinorUnits = 0;
    lineElements.forEach((lineElement) => {
      const signedLine = signed.lines[signedLineKey(resolveNetsuiteId(lineElement), lineElement.dataset.b2bLotStatus)];
      const unitMinorUnits = Number(signedLine.unit_price_minor);
      const lineMinorUnits = unitMinorUnits * rowQuantity(lineElement);
      netMinorUnits += lineMinorUnits;
      paintSignedRow(lineElement, unitMinorUnits / scale, lineMinorUnits / scale, currencyName);
    });
    let totalMinorUnits = netMinorUnits;
    const taxRate = netElements.length > 0 || taxElements.length > 0 ? cartTaxRate() : null;
    settlePaint(netElements, formatCurrency(netMinorUnits / scale, currencyName));
    if (taxRate === null) {
      settlePaint(taxElements, NO_AMOUNT_TEXT);
    } else {
      const taxMinorUnits = Math.round((netMinorUnits * taxRate) / 100);
      totalMinorUnits = netMinorUnits + taxMinorUnits;
      settlePaint(taxElements, formatCurrency(taxMinorUnits / scale, currencyName));
    }
    paintTotal(formatCurrency(totalMinorUnits / scale, currencyName));
  }

  // Marks every element painted and out of its skeleton and state, writing `text` first unless it is null.
  function settlePaint(elements, text) {
    elements.forEach((element) => {
      if (text !== null) element.textContent = text;
      element.setAttribute(PAINTED_ATTRIBUTE, '');
      element.removeAttribute('data-b2b-loading');
      element.removeAttribute(PRICE_STATE_ATTRIBUTE);
    });
  }

  // The cart's tax rate in percent, or null when there is no tax to estimate. The rate belongs to the
  // market Liquid rendered the cart in (Shopify's, from the company location's shipping address), and the
  // prices to the country of the selected branch. When those disagree, a location set up in the wrong
  // country, no tax is estimated rather than one country's tax on the other country's prices.
  let taxCountryMismatchLogged = false;
  function cartTaxRate() {
    const container = document.querySelector('[data-b2b-tax-rate]');
    if (container === null) return null;
    const rate = Number(container.getAttribute('data-b2b-tax-rate'));
    if (!Number.isFinite(rate) || rate <= 0) return null;
    const country = container.getAttribute('data-b2b-tax-country');
    const pricingCountry = getEffectiveCountry();
    if (country !== pricingCountry) {
      // Every cart re-render comes back here; one line says it.
      if (!taxCountryMismatchLogged) {
        logWarn(`cart tax not estimated: the rate is for ${country}, the prices are for ${pricingCountry}`);
        taxCountryMismatchLogged = true;
      }
      return null;
    }
    return rate;
  }

  async function renderPrices(root) {
    if (!bootstrapIsUsable()) {
      clearShimmer();
      updateCartTotals();
      return;
    }

    const byProductId = collectPriceElements(root);
    if (byProductId.size === 0) {
      logDebug('no price elements with data-netsuite-id on this page');
      clearShimmer();
      updateCartTotals();
      return;
    }

    // Logged-out OR pending approval: never fetch or show prices. Force every price element to its
    // "Consultar" unpriced label (applyLine's status!=='ok' branch writes data-b2b-unpriced-label +
    // adds .b2b-unpriced) and bail before any pricing API call. Server-rendered cards already carry
    // the label; this is the catch-all for any other surface so no price ever appears in the UI.
    // Pending (logged in, no approved CompanyLocation) gets the same treatment as logged-out —
    // wholesale prices and stock only unlock once the account is approved.
    if (BOOTSTRAP.isLoggedIn === false || BOOTSTRAP.isPending === true) {
      byProductId.forEach((elements, productId) => {
        applyLine(elements, { status: 'unpriced', product_id: Number(productId) });
      });
      clearShimmer();
      updateCartTotals();
      return;
    }

    const productIds = Array.from(byProductId.keys());
    const cache = window.LudipekB2BCache;
    const region = getSelectedRegion();
    const customerIdForCache = getCustomerIdForPricing();
    const effectiveCountry = getEffectiveCountry();
    const { hits, stale, misses } = cache
      ? cache.getMany(customerIdForCache, effectiveCountry, region, productIds)
      : { hits: {}, stale: {}, misses: productIds };

    // Fresh and stale both paint straight away. A stale entry is a real price from at most a few
    // hours ago; showing it now and correcting it a moment later beats a second and a half of
    // shimmer, and the buyer is never charged this number — /cart/sign resolves what they pay.
    const paintFromCache = (productId, entry) => {
      // Include product_id explicitly — cache entries don't store it (the cache key is product_id),
      // and applyLine's downstream b2b:base-price-updated dispatch reads line.product_id. Without
      // this, cache-hit dispatches fire with productId: NaN and PDP-side listeners early-return.
      applyLine(byProductId.get(Number(productId)), {
        ...entry,
        status: 'ok',
        product_id: Number(productId),
      });
    };
    Object.entries(hits).forEach(([productId, entry]) => paintFromCache(productId, entry));
    Object.entries(stale).forEach(([productId, entry]) => paintFromCache(productId, entry));

    const staleIds = Object.keys(stale).map(Number);
    const toFetch = misses.concat(staleIds);

    if (toFetch.length === 0) {
      logDebug(`all ${productIds.length} prices served from cache`);
      clearShimmer();
      updateCartTotals();
      return;
    }

    // Nothing left shimmering once the stale paint has happened — the refresh is silent from here.
    if (misses.length === 0) {
      logDebug(`${staleIds.length} stale prices painted, revalidating in the background`);
      clearShimmer();
      updateCartTotals();
    }

    try {
      const payload = await fetchQuotes(toFetch);
      if (!payload || !Array.isArray(payload.lines)) {
        logWarn(`unexpected response shape, ${misses.length} cold prices marked unavailable`, payload);
        markFailed(byProductId, misses);
        return;
      }
      payload.lines.forEach((line) => {
        const elements = byProductId.get(Number(line.product_id));
        if (elements) applyLine(elements, line);
      });
      if (cache) {
        cache.setMany(customerIdForCache, effectiveCountry, region, payload.lines);
      }
      // The API answers one line per requested id. A cold id it skipped anyway would otherwise
      // shimmer until the watchdog gives up on it.
      const answeredIds = new Set(payload.lines.map((line) => Number(line && line.product_id)));
      const unansweredIds = misses.filter((productId) => !answeredIds.has(Number(productId)));
      if (unansweredIds.length > 0) {
        logWarn(`${unansweredIds.length} requested prices missing from the response, marked unavailable`, {
          product_ids: unansweredIds,
        });
        markFailed(byProductId, unansweredIds);
      }
      logDebug(
        `fetched ${toFetch.length} prices (${misses.length} cold, ${staleIds.length} revalidated), ` +
          `${Object.keys(hits).length} fresh from cache`
      );
    } catch (error) {
      // A failed revalidation is not a failure the buyer needs to see: the stale price is still on
      // screen and still honest enough for a display surface. A cold miss has nothing on screen, so
      // it goes to the failed state rather than shimmering forever.
      logWarn(
        `price fetch failed, ${misses.length} cold prices marked unavailable, ` +
          `${staleIds.length} last known prices remain visible`,
        { error: String(error) },
      );
      markFailed(byProductId, misses);
    } finally {
      clearShimmer();
      updateCartTotals();
    }
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  // Watch for price elements injected after initial render — paginated grid lazy-injects more
  // cards on scroll, and PDP recommendations fetch async. Without this observer those elements
  // keep their Shopify-default price. It also watches for painted elements losing PAINTED_ATTRIBUTE,
  // which is how an in-place re-render (Horizon's morph) shows up.
  let rescanScheduled = false;
  function scheduleRescan() {
    if (rescanScheduled) return;
    rescanScheduled = true;
    // A microtask, not requestAnimationFrame or a timer: a page that is not being rendered (a background
    // tab) runs no frames, so a rescan waiting on one left Shopify's numbers in place, and a timer can
    // let a frame show them first. The morph or injection that triggered the rescan runs synchronously,
    // so it has finished by the time the observer calls us, and the cached paint lands before the frame.
    queueMicrotask(() => {
      rescanScheduled = false;
      renderPrices(document);
    });
  }

  function startObserving() {
    const observer = new MutationObserver((mutations) => {
      let rescan = false;
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          // Only a removal counts: every paint re-sets the marker, which must not trigger another paint.
          if (!mutation.target.hasAttribute(PAINTED_ATTRIBUTE)) {
            collectedElements.delete(mutation.target);
            rescan = true;
          }
          continue;
        }
        if (rescan) continue;
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(PRICE_SELECTOR) || node.querySelector?.(PRICE_SELECTOR)) {
            rescan = true;
            break;
          }
        }
      }
      if (rescan) scheduleRescan();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [PAINTED_ATTRIBUTE],
    });
  }

  onReady(() => {
    renderPrices(document);
    startObserving();
  });

  // Re-render when cart or collection content changes (cart drawer, infinite scroll, filters).
  // Horizon fires 'cart:update' (CartAddEvent/CartUpdateEvent share that eventName); the older
  // 'cart:updated' is kept as a harmless alias. Defer to the next frame so the cart-items-component has
  // finished morphing its section before we re-overlay B2B prices (the MutationObserver is the backstop).
  const reRenderCart = () => requestAnimationFrame(() => renderPrices(document));
  document.addEventListener('cart:update', reRenderCart);
  document.addEventListener('cart:updated', reRenderCart);
  document.addEventListener('section:reload', (event) => {
    const target = event && event.target && event.target.nodeType === 1 ? event.target : document;
    renderPrices(target);
  });

  // A new signature, or a cleared one, changes what the cart summary can show (see updateCartTotals).
  document.addEventListener('b2b:cart-signed', (event) => {
    latestSignedPayload = (event.detail && event.detail.payload) || '';
    updateCartTotals();
  });

  // Retry every price in the failed state, including skeletons the watchdog gave up on (a PDP price
  // card, the cart total). Clearing the state turns them back into plain skeletons, and releasing them
  // from collectedElements lets the render below collect and fetch them again. Called by the
  // "reintenta" button the watchdog renders, and when the browser comes back online.
  function retryFailedPrices() {
    const failedElements = document.querySelectorAll(`[${PRICE_STATE_ATTRIBUTE}="${FAILED_STATE}"]`);
    if (failedElements.length === 0) return;
    failedElements.forEach((element) => {
      element.removeAttribute(PRICE_STATE_ATTRIBUTE);
      collectedElements.delete(element);
    });
    logDebug(`retrying ${failedElements.length} unavailable prices`);
    renderPrices(document);
  }
  window.addEventListener('online', retryFailedPrices);

  window.LudipekB2BPricing = { renderPrices, retry: retryFailedPrices };
})();
